import cx from "classnames";

import BackgroundPattern from "assets/images/BackgroundPattern.svg";
import { DesktopHeroImage } from "atoms/desktop-hero/DesktopHero";
import { ImagePreloader } from "atoms/image-preloader/ImagePreloader";

export interface ShopListHeaderProps {
  title: string;
  subtitle?: string;
  backgroundImageUrl?: string;
  backgroundColor?: string;
  bgGradient?: boolean;
  backgroundPattern?: boolean;
}

export const ShopListHeader = ({
  title,
  subtitle,
  backgroundImageUrl,
  backgroundPattern,
  backgroundColor,
  bgGradient,
}: ShopListHeaderProps) => {
  // Single source of truth for visual decisions
  const hasImage = !!backgroundImageUrl;
  const useLightText = bgGradient || hasImage || backgroundPattern;

  // Consolidate background styling with clear precedence
  const gradientStyle = bgGradient
    ? backgroundPattern
      ? "linear-gradient(82deg, transparent, rgb(255,255,255,.2) 34%, rgb(255,255,255,.2) 46%, transparent)"
      : hasImage
        ? "linear-gradient(90deg, rgba(0, 0, 0, 0.40) 15.23%, rgba(0, 0, 0, 0.36) 32.6%, rgba(0, 0, 0, 0.29) 44.92%, rgba(0, 0, 0, 0.00) 78.16%)"
        : "linear-gradient(90deg, rgba(0, 0, 0, 0.20) 15.23%, rgba(0, 0, 0, 0.18) 32.6%, rgba(0, 0, 0, 0.14) 44.92%, rgba(0, 0, 0, 0.00) 78.16%)"
    : "";

  const backgroundStyle = {
    backgroundColor,
    backgroundImage:
      [gradientStyle, hasImage && `url(${backgroundImageUrl})`].filter(Boolean).join(", ") ||
      undefined,
  };

  const renderContent = () => (
    <>
      {!!backgroundPattern && (
        <BackgroundPattern className="absolute inset-0 w-full scale-150 opacity-10" />
      )}
      <div className="flex h-full w-full flex-col justify-center">
        <div
          className={cx(
            "mx-auto w-full max-w-6xl px-6 desk:px-8",
            hasImage && "desk:max-w-none desk:px-0",
          )}
        >
          <div
            className={cx(
              backgroundImageUrl ? "w-1/2 desk:w-full desk:max-w-xl" : "w-full desk:max-w-2xl",
            )}
          >
            <div
              className={cx(
                "font-serif",
                "text-h2 desk:text-h1",
                useLightText ? "text-white" : "text-primary",
                hasImage && "desk:text-primary desk:[text-shadow:none]",
                bgGradient ? "text-shadow-subtle" : "",
              )}
            >
              {title}
            </div>
            {subtitle && (
              <div
                className={cx(
                  "pt-3",
                  "text-body2",
                  useLightText ? "text-white" : "text-primary",
                  hasImage && "desk:text-primary desk:[text-shadow:none]",
                  bgGradient ? "text-shadow-subtle" : "",
                )}
              >
                {subtitle}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex flex-col">
      <div
        className={cx(
          "relative h-[200px] w-full overflow-hidden",
          hasImage
            ? "desk:mx-auto desk:my-6 desk:grid desk:h-auto desk:w-[calc(100%-4rem)] desk:max-w-6xl desk:grid-cols-[minmax(0,1fr)_minmax(0,16rem)] desk:items-center desk:gap-12 desk:rounded-2xl desk:bg-background-two desk:p-10"
            : "desk:h-[220px]",
        )}
      >
        {hasImage ? (
          <ImagePreloader
            url={backgroundImageUrl}
            fade={300}
            lazy={false}
            className="absolute inset-0 flex h-full w-full desk:hidden"
            placeholder={
              <div
                className="h-full bg-cover bg-center"
                style={{
                  backgroundColor,
                  backgroundImage: bgGradient
                    ? "linear-gradient(90deg, rgba(0, 0, 0, 0.40) 15.23%, rgba(0, 0, 0, 0.36) 32.6%, rgba(0, 0, 0, 0.29) 44.92%, rgba(0, 0, 0, 0.00) 78.16%)"
                    : undefined,
                }}
              />
            }
          >
            <div
              className="h-full w-full flex-1 bg-cover bg-center"
              style={{
                backgroundImage: `${
                  gradientStyle ? gradientStyle + ", " : ""
                }url(${backgroundImageUrl})`,
                backgroundColor,
              }}
            />
          </ImagePreloader>
        ) : (
          <div className="h-full w-full bg-cover bg-center" style={backgroundStyle} />
        )}
        <div className={cx("absolute inset-0 z-10", hasImage && "desk:static desk:inset-auto")}>
          {renderContent()}
        </div>
        {backgroundImageUrl && <DesktopHeroImage url={backgroundImageUrl} desktopUrl={null} />}
      </div>
    </div>
  );
};
